<template>
  <canvas ref="canvas" class="particle-field" aria-hidden="true"></canvas>
</template>

<script>
export default {
  name: 'ParticleField',
  props: {
    variant: {
      type: String,
      default: 'dark'
    },
    count: {
      type: Number,
      default: 48
    }
  },
  data() {
    return {
      raf: null,
      particles: [],
      width: 0,
      height: 0
    }
  },
  mounted() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    this.init()
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    if (this.raf) cancelAnimationFrame(this.raf)
  },
  methods: {
    init() {
      const canvas = this.$refs.canvas
      if (!canvas) return
      if (this.raf) {
        cancelAnimationFrame(this.raf)
        this.raf = null
      }
      const parent = canvas.parentElement
      this.width = parent.clientWidth
      this.height = parent.clientHeight
      canvas.width = this.width
      canvas.height = this.height

      this.particles = Array.from({ length: this.count }, () => ({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        r: Math.random() * 1.6 + 0.4,
        vx: (Math.random() - 0.5) * 0.18,
        vy: (Math.random() - 0.5) * 0.18,
        alpha: Math.random() * 0.35 + 0.08
      }))

      this.tick()
    },
    handleResize() {
      this.init()
    },
    tick() {
      const canvas = this.$refs.canvas
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      const isDark = this.variant === 'dark'
      const dotColor = isDark ? '255, 255, 255' : '4, 120, 87'
      const lineColor = isDark ? '255, 255, 255' : '4, 120, 87'
      const linkDist = isDark ? 120 : 100

      ctx.clearRect(0, 0, this.width, this.height)

      this.particles.forEach(p => {
        p.x += p.vx
        p.y += p.vy
        if (p.x < 0 || p.x > this.width) p.vx *= -1
        if (p.y < 0 || p.y > this.height) p.vy *= -1

        ctx.beginPath()
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(${dotColor}, ${p.alpha})`
        ctx.fill()
      })

      for (let i = 0; i < this.particles.length; i++) {
        for (let j = i + 1; j < this.particles.length; j++) {
          const a = this.particles[i]
          const b = this.particles[j]
          const dx = a.x - b.x
          const dy = a.y - b.y
          const dist = Math.sqrt(dx * dx + dy * dy)
          if (dist < linkDist) {
            ctx.beginPath()
            ctx.moveTo(a.x, a.y)
            ctx.lineTo(b.x, b.y)
            ctx.strokeStyle = `rgba(${lineColor}, ${(1 - dist / linkDist) * (isDark ? 0.06 : 0.08)})`
            ctx.stroke()
          }
        }
      }

      this.raf = requestAnimationFrame(this.tick)
    }
  }
}
</script>

<style scoped>
.particle-field {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}
</style>
